import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useEntitlements } from "../contexts/EntitlementsContext";
import { formatNum, apiErr } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ReferenceLine, CartesianGrid,
} from "recharts";
import {
  ClipboardCheck, Sparkles, TrendingUp, TrendingDown, Minus, Users, ShieldCheck,
  AlertTriangle, Leaf, Target, FileCheck2, Plus, Send, CheckCircle2, XCircle, History, FileSearch,
} from "lucide-react";
import { toast } from "sonner";

const ISSUE_PILLAR = {
  "GHG Emissions": "Environment", "Energy Management": "Environment", "Water Management": "Environment",
  "Waste & Circularity": "Environment", "Workforce Diversity": "Social", "Health & Safety": "Social",
  "Supplier Sustainability": "Social", "Governance & Ethics": "Governance", "Data Governance": "Governance",
  "Community": "Social",
};
function evidenceHref(k) {
  const pillar = ISSUE_PILLAR[k.material_issue] || "Governance";
  const p = new URLSearchParams({ pillar, category: k.material_issue || "", activity: k.kpi_name || "", range: "all" });
  return `/data-management?${p.toString()}`;
}

const RAG = { green: "text-[#00E65B]", amber: "text-[#FFB800]", red: "text-red-500", grey: "text-muted-foreground" };
const RAG_BG = { green: "bg-[#00E65B]", amber: "bg-[#FFB800]", red: "bg-red-500", grey: "bg-muted-foreground/40" };
const RAG_LABEL = { green: "On track", amber: "At risk", red: "Off track", grey: "—" };
const PILLAR = { E: { label: "Environmental", icon: Leaf }, S: { label: "Social", icon: Users }, G: { label: "Governance", icon: ShieldCheck } };
const KPI_STATUS = {
  draft: { label: "Draft", tone: "outline" },
  submitted: { label: "Submitted", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
};
const APPROVER_ROLES = ["tenant_admin", "esg_manager", "super_admin"];

function Travel({ t }) {
  if (!t) return null;
  const map = { improving: ["text-[#00E65B]", TrendingUp], worsening: ["text-red-500", TrendingDown], flat: ["text-muted-foreground", Minus] };
  const [c, Icon] = map[t] || map.flat;
  return <span className={`inline-flex items-center gap-1 text-[11px] ${c}`}><Icon className="h-3 w-3" />{t}</span>;
}

function ScoreRing({ score, rag, size = "text-5xl" }) {
  return <span className={`font-mono ${size} font-bold tracking-tighter ${RAG[rag]}`}>{score ?? "—"}<span className="text-base text-muted-foreground">/100</span></span>;
}

/* ----------------------------- KPI create / edit ----------------------------- */
function KpiEditor({ open, onClose, kpi, onSaved }) {
  const blank = { kpi_name: "", material_issue: "", owner: "", unit: "", baseline_value: "", baseline_period: "", current_value: "", target_value: "", target_direction: "decrease", target_period: "2030", frameworks: "", mandatory: false };
  const [f, setF] = useState(blank);
  useEffect(() => {
    if (!open) return;
    setF(kpi ? { ...kpi, frameworks: (kpi.frameworks || []).join(", "), baseline_value: kpi.baseline_value ?? "", current_value: kpi.current_value ?? "", target_value: kpi.target_value ?? "" } : blank);
  }, [open, kpi]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const body = {
      kpi_name: f.kpi_name, material_issue: f.material_issue, owner: f.owner, unit: f.unit,
      baseline_value: f.baseline_value === "" ? null : Number(f.baseline_value),
      current_value: f.current_value === "" ? null : Number(f.current_value),
      target_value: f.target_value === "" ? null : Number(f.target_value),
      baseline_period: f.baseline_period, target_direction: f.target_direction, target_period: f.target_period,
      frameworks: f.frameworks ? f.frameworks.split(",").map((x) => x.trim()).filter(Boolean) : [],
      mandatory: !!f.mandatory,
    };
    try {
      if (kpi) await api.patch(`/scorecard/kpis/${kpi.id}`, body);
      else await api.post("/scorecard/kpis", body);
      toast.success(kpi ? "KPI updated" : "KPI registered as draft");
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="kpi-editor-dialog">
        <DialogHeader><DialogTitle className="font-display">{kpi ? "Edit KPI" : "Register new KPI"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>KPI name</Label><Input value={f.kpi_name} onChange={(e) => set("kpi_name", e.target.value)} data-testid="kpi-name-input" /></div>
          <div><Label>Material issue</Label><Input value={f.material_issue} onChange={(e) => set("material_issue", e.target.value)} placeholder="e.g. Climate Change" data-testid="kpi-issue-input" /></div>
          <div><Label>Owner</Label><Input value={f.owner} onChange={(e) => set("owner", e.target.value)} /></div>
          <div><Label>Unit</Label><Input value={f.unit} onChange={(e) => set("unit", e.target.value)} placeholder="tCO₂e, %, kWh…" /></div>
          <div><Label>Direction</Label>
            <Select value={f.target_direction} onValueChange={(v) => set("target_direction", v)}>
              <SelectTrigger data-testid="kpi-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="decrease">Decrease (lower is better)</SelectItem>
                <SelectItem value="increase">Increase (higher is better)</SelectItem>
                <SelectItem value="achieve_zero">Achieve zero</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label>Baseline value</Label><Input type="number" value={f.baseline_value} onChange={(e) => set("baseline_value", e.target.value)} data-testid="kpi-baseline-input" /></div>
          <div><Label>Baseline period</Label><Input value={f.baseline_period} onChange={(e) => set("baseline_period", e.target.value)} placeholder="FY2022" /></div>
          <div><Label>Current value</Label><Input type="number" value={f.current_value} onChange={(e) => set("current_value", e.target.value)} /></div>
          <div><Label>Target value</Label><Input type="number" value={f.target_value} onChange={(e) => set("target_value", e.target.value)} data-testid="kpi-target-input" /></div>
          <div><Label>Target period</Label><Input value={f.target_period} onChange={(e) => set("target_period", e.target.value)} placeholder="2030" /></div>
          <div className="col-span-2"><Label>Frameworks (comma-separated)</Label><Input value={f.frameworks} onChange={(e) => set("frameworks", e.target.value)} placeholder="GRI, SASB, IFRS S2" /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={!!f.mandatory} onChange={(e) => set("mandatory", e.target.checked)} className="accent-[hsl(var(--accent))]" /> Mandatory disclosure KPI</label>
        </div>
        <div className="flex justify-end"><Button variant="primary" onClick={save} disabled={!f.kpi_name || !f.material_issue} data-testid="kpi-save-btn">{kpi ? "Save changes" : "Register KPI"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- KPI audit trail ----------------------------- */
const ACTION_TONE = {
  Approved: "text-[#00E65B]", Rejected: "text-red-500", "Submitted for approval": "text-[#FFB800]",
  Created: "text-muted-foreground", Edited: "text-foreground", "Actual updated": "text-accent",
};
function AuditTrail({ entries, approvedBy, approvedAt }) {
  const list = [...(entries || [])].reverse();
  return (
    <div className="card-flat p-3" data-testid="scorecard-audit-trail">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow inline-flex items-center gap-1"><History className="h-3.5 w-3.5 text-accent" /> Governance / Audit trail</div>
        {approvedBy && <Badge variant="success" className="text-[9px]">Approved by {approvedBy}{approvedAt ? ` · ${approvedAt.slice(0, 10)}` : ""}</Badge>}
      </div>
      {list.length ? (
        <ol className="relative border-l border-border ml-1 space-y-3">
          {list.map((e, i) => (
            <li key={i} className="ml-4" data-testid={`audit-entry-${i}`}>
              <span className="absolute -left-[5px] mt-1 h-2.5 w-2.5 rounded-full bg-accent" />
              <div className="flex items-center justify-between gap-2">
                <span className={`text-xs font-semibold ${ACTION_TONE[e.action] || "text-foreground"}`}>{e.action}</span>
                <span className="text-[10px] text-muted-foreground font-mono">{(e.at || "").replace("T", " ").slice(0, 16)}</span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                {e.user}{e.role ? ` · ${e.role.replace(/_/g, " ")}` : ""}{e.detail ? ` — ${e.detail}` : ""}
              </div>
            </li>
          ))}
        </ol>
      ) : <p className="text-xs text-muted-foreground">No recorded actions yet. Submissions, approvals, edits and actual-value updates are logged here for IFRS S2 / SGX assurance.</p>}
    </div>
  );
}

/* ----------------------------- KPI detail dialog ----------------------------- */
function KpiDialog({ kpiId, open, onClose, onChanged }) {
  const navigate = useNavigate();
  const [k, setK] = useState(null);
  const [commentary, setCommentary] = useState("");
  const [busy, setBusy] = useState(false);
  const [actual, setActual] = useState("");

  useEffect(() => {
    if (!open || !kpiId) return;
    setK(null); setCommentary("");
    api.get(`/scorecard/kpis/${kpiId}`).then((r) => { setK(r.data); setActual(r.data.current_value ?? ""); }).catch((e) => toast.error(apiErr(e)));
  }, [open, kpiId]);

  const gen = async () => { setBusy(true); try { const r = await api.post(`/scorecard/kpis/${kpiId}/ai/commentary`); setCommentary(r.data.commentary); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const saveActual = async () => {
    try { const r = await api.post(`/scorecard/kpis/${kpiId}/actual`, { current_value: Number(actual), commentary: commentary || undefined }); setK(r.data); toast.success("Actual updated"); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="scorecard-kpi-dialog">
        {!k ? <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">{k.kpi_name}
                <Badge variant="outline" className="text-[10px]">{k.material_issue}</Badge></DialogTitle>
            </DialogHeader>
            <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => { onClose(); navigate(evidenceHref(k)); }} data-testid="kpi-dialog-evidence-btn">
              <FileSearch className="h-3.5 w-3.5" /> View evidence records (FY2023 baseline → current)
            </Button>
            <div className="grid grid-cols-3 gap-3">
              <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Baseline</div><div className="font-mono text-lg font-bold">{formatNum(k.baseline_value, { decimals: 1 })}<span className="text-xs text-muted-foreground ml-1">{k.unit}</span></div></div>
              <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Current</div><div className="font-mono text-lg font-bold">{formatNum(k.current_value, { decimals: 1 })}<span className="text-xs text-muted-foreground ml-1">{k.unit}</span></div></div>
              <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Target</div><div className="font-mono text-lg font-bold">{formatNum(k.target_value, { decimals: 1 })}<span className="text-xs text-muted-foreground ml-1">{k.unit}</span></div></div>
            </div>
            <div className="card-flat p-3">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Progress to target ({k.target_direction})</span>
                <span className={`font-mono font-bold ${RAG[k.rag]}`}>{k.progress_pct}% · score {k.kpi_score} · <Travel t={k.direction_of_travel} /></span>
              </div>
              <Progress value={k.progress_pct} className="h-2" />
              <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
                <span>Owner: {k.owner || "—"}</span><span>Data quality: {k.data_quality_pct}%</span>
                <span>{(k.frameworks || []).join(", ")}</span>
              </div>
              {k.period_matched && (
                <div className="mt-2 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/10 px-2 py-1.5 text-[11px] text-[#8a5a00] dark:text-[#FFB800]" data-testid="kpi-ytd-basis-note">
                  YTD-vs-YTD basis: {k.scored_period_note || `current (${k.current_as_of || "year-to-date"}) is scored against a period-matched (pro-rated) baseline & target`}.
                </div>
              )}
            </div>
            <div className="card-flat p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow inline-flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Trend vs target</div>
                <Badge variant={k.on_track ? "success" : "danger"} className="text-[9px]" data-testid="scorecard-ontrack-badge">{k.on_track ? "On track" : "Off track"}</Badge>
              </div>
              {(k.trend || []).length >= 2 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={k.trend} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RTooltip contentStyle={{ background: "hsl(var(--surface))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                    {k.target_value != null && <ReferenceLine y={k.target_value} stroke="#00E65B" strokeDasharray="4 4" label={{ value: `Target ${k.target_value}`, fontSize: 9, fill: "#00E65B", position: "insideTopRight" }} />}
                    <Line type="monotone" dataKey="value" stroke={k.on_track ? "#00E65B" : "#FFB800"} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground">Record more actual values over time to plot the trend.</p>}
            </div>
            <div className="card-flat p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI commentary</div>
                <Button size="sm" variant="primary" onClick={gen} disabled={busy} data-testid="scorecard-ai-commentary">{busy ? "…" : commentary ? "Regenerate" : "Generate"}</Button>
              </div>
              {commentary ? <p className="text-sm leading-relaxed" data-testid="scorecard-commentary-text">{commentary}</p>
                : <p className="text-sm text-muted-foreground">{k.commentary || "Generate an explanation of this KPI's progress and the priority action."}</p>}
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1"><label className="label-eyebrow text-[10px]">Update current actual ({k.unit})</label>
                <Input type="number" value={actual} onChange={(e) => setActual(e.target.value)} data-testid="scorecard-actual-input" /></div>
              <Button variant="outline" onClick={saveActual} data-testid="scorecard-save-actual">Save actual</Button>
            </div>
            <AuditTrail entries={k.audit_trail} approvedBy={k.approved_by} approvedAt={k.approved_at} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- page ----------------------------- */
function ScoreIntelLink({ sip }) {
  const { entitled } = useEntitlements();
  const navigate = useNavigate();
  if (!entitled("score_intelligence")) return null;
  return (
    <div className="card-flat p-4 border-accent/40 bg-accent/5" data-testid="scorecard-si-link">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="label-eyebrow mb-1 flex items-center gap-1.5"><FileSearch className="h-3.5 w-3.5 text-accent" /> Source of truth · Score Intelligence</div>
          {sip?.exists ? (
            <div className="text-sm">
              Authoritative performance is now governed by Score Intelligence —
              <span className="font-mono font-bold"> {sip.overall.score}</span>
              <span className="text-muted-foreground"> ({sip.overall.coverage}% covered, range {sip.overall.range[0]}–{sip.overall.range[1]})</span>
              <span className="text-muted-foreground"> · E {sip.pillars.E.score ?? "—"} · S {sip.pillars.S.score ?? "—"} · G {sip.pillars.G.score ?? "—"} · CCI {sip.comparable_core_index.score}</span>
              <div className="text-[11px] text-muted-foreground mt-0.5">{sip.entity} · {sip.period} · v{sip.published_version}. This scorecard is a linked operational view — coverage-normalised scoring lives in Score Intelligence.</div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">This scorecard is a linked view. Publish a Score Intelligence assessment to make its coverage-normalised performance the authoritative figure.</div>
          )}
        </div>
        <Button variant="primary" size="sm" onClick={() => navigate("/score-intelligence")} data-testid="scorecard-open-si-btn">View in Score Intelligence</Button>
      </div>
    </div>
  );
}

export default function ESGScorecard() {
  const { user } = useAuth();
  const { entitled } = useEntitlements();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [sip, setSip] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [active, setActive] = useState(null);
  const [board, setBoard] = useState("");
  const [boardBasis, setBoardBasis] = useState("");
  const [boardBusy, setBoardBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editKpi, setEditKpi] = useState(null);
  const canApprove = APPROVER_ROLES.includes(user.role);

  const load = useCallback(() => {
    Promise.all([api.get("/scorecard/dashboard"), api.get("/scorecard/kpis")])
      .then(([dd, kk]) => { setD(dd.data); setKpis(kk.data); }).catch(() => setD({ empty: true }));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!entitled("score_intelligence")) return;
    api.get("/score-intel/latest-published").then(({ data }) => setSip(data)).catch(() => {});
  }, [entitled]);

  const siAvailable = !!sip?.exists;
  const [headlineSource, setHeadlineSource] = useState("score_intel");
  const siGoverns = siAvailable && headlineSource === "score_intel";
  const headOverall = siGoverns && sip.overall?.score != null ? sip.overall.score : d?.overall_score;
  const headPillars = siGoverns
    ? { E: sip.pillars?.E?.score ?? d?.pillars?.E, S: sip.pillars?.S?.score ?? d?.pillars?.S, G: sip.pillars?.G?.score ?? d?.pillars?.G }
    : (d?.pillars || {});

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const want = searchParams.get("kpi");
    if (want && kpis.length) {
      const match = kpis.find((k) => k.kpi_name === want);
      if (match) {
        setActive(match.id);
        const sp = new URLSearchParams(searchParams); sp.delete("kpi"); setSearchParams(sp, { replace: true });
      }
    }
  }, [kpis, searchParams, setSearchParams]);

  const kpiAction = async (id, action) => {
    try { await api.post(`/scorecard/kpis/${id}/${action}`); toast.success(`KPI ${action === "submit" ? "submitted for approval" : action + "d"}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const downloadPack = async () => {
    try {
      const r = await api.get("/baseline/evidence-pack.pdf", { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = u; a.download = "esg_evidence_pack.pdf"; a.click(); URL.revokeObjectURL(u);
      toast.success("Evidence pack downloaded");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const genBoard = async () => { setBoardBusy(true); try { const r = await api.post("/scorecard/ai/board-summary", null, { params: { source: siGoverns ? "score_intel" : "scorecard" } }); setBoard(r.data.summary); setBoardBasis(r.data.basis || ""); } catch (e) { toast.error(apiErr(e)); } finally { setBoardBusy(false); } };

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading scorecard…</div>;
  if (d.empty) return (
    <div className="p-8 space-y-4">
      <div className="card-flat p-12 text-center">
        <h2 className="font-display text-2xl font-bold">No approved KPIs yet.</h2>
        <p className="text-muted-foreground mt-2">Register material-issue KPIs with baselines and targets, then approve them to build your ESG scorecard.</p>
        <Button variant="primary" className="mt-4" onClick={() => { setEditKpi(null); setEditorOpen(true); }} data-testid="scorecard-empty-register-btn"><Plus className="h-4 w-4" /> Register KPI</Button>
      </div>
      <KpiEditor open={editorOpen} onClose={() => setEditorOpen(false)} kpi={editKpi} onSaved={load} />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="esg-scorecard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Scenarios & Risks · ESG Scorecard · FY2024</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><ClipboardCheck className="h-8 w-8 text-accent" /> ESG Scorecard</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Material-issue performance against owned, time-bound targets — actual vs baseline vs target, with RAG status and an aggregated ESG score.</p>
        </div>
      </div>

      <ScoreIntelLink sip={sip} />

      {siAvailable && (
        <div className="flex items-center gap-2 flex-wrap -mt-2" data-testid="scorecard-source-toggle">
          <span className="text-[11px] text-muted-foreground">Headline scores source:</span>
          <div className="inline-flex rounded-sm border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setHeadlineSource("score_intel")}
              className={`text-[11px] px-2.5 py-1 ${headlineSource === "score_intel" ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground hover:bg-muted"}`}
              data-testid="scorecard-source-si">Score Intelligence</button>
            <button
              type="button"
              onClick={() => setHeadlineSource("scorecard")}
              className={`text-[11px] px-2.5 py-1 border-l border-border ${headlineSource === "scorecard" ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground hover:bg-muted"}`}
              data-testid="scorecard-source-own">My scorecard</button>
          </div>
        </div>
      )}

      {siAvailable && (
        <div className="text-[11px] text-muted-foreground -mt-1" data-testid="scorecard-si-governed-note">
          {siGoverns
            ? "Headline scores below are governed by Score Intelligence (coverage-normalised) so they match the banner above. KPI-level tiles further down remain the operational scorecard view."
            : "Headline scores below use your own approved ESG Scorecard (KPI-weighted). Switch to Score Intelligence to see the coverage-normalised, add-on figures."}
        </div>
      )}

      {/* headline */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="card-flat p-5 group" data-testid="scorecard-overall">
          <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Overall ESG score <ProvenanceTrigger pkey="scorecard.overall" className="ml-auto" /></div>
          <ScoreRing score={headOverall} rag={d.overall_rag} />
          <Progress value={headOverall} className="h-2 mt-3" />
          <div className="text-xs text-muted-foreground mt-1.5">{RAG_LABEL[d.overall_rag]} · {d.total_kpis} {d.total_kpis === 1 ? "KPI" : "KPIs"} · {d.off_track_count} off-track{siGoverns ? " · via Score Intelligence" : ""}</div>
        </div>
        {Object.entries(headPillars).map(([p, sc]) => {
          const Icon = PILLAR[p].icon; const rag = sc >= 75 ? "green" : sc >= 40 ? "amber" : "red";
          return (
            <div key={p} className="card-flat p-5 group" data-testid={`scorecard-pillar-${p}`}>
              <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {PILLAR[p].label} <ProvenanceTrigger pkey="scorecard.pillar" pp={p} className="ml-auto" /></div>
              <ScoreRing score={sc} rag={rag} />
              <Progress value={sc} className="h-2 mt-3" />
            </div>
          );
        })}
      </div>

      <Tabs defaultValue="scorecard">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="scorecard" data-testid="scorecard-tab-scorecard">Scorecard</TabsTrigger>
          <TabsTrigger value="register" data-testid="scorecard-tab-register">KPI Register</TabsTrigger>
          <TabsTrigger value="owners" data-testid="scorecard-tab-owners">Owners</TabsTrigger>
          <TabsTrigger value="frameworks" data-testid="scorecard-tab-frameworks">Frameworks</TabsTrigger>
          <TabsTrigger value="offtrack" data-testid="scorecard-tab-offtrack">Off-track {d.off_track_count > 0 && <span className="ml-1 text-red-500">●</span>}</TabsTrigger>
          <TabsTrigger value="board" data-testid="scorecard-tab-board">Board Summary</TabsTrigger>
        </TabsList>

        {/* Scorecard — material issue cards */}
        <TabsContent value="scorecard" className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.material_issues.map((iss) => (
              <div key={iss.material_issue} className="card-flat p-4" data-testid={`scorecard-issue-${iss.material_issue.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="label-eyebrow text-[10px]">{iss.pillar_name}</div>
                    <div className="font-display font-semibold">{iss.material_issue}</div>
                  </div>
                  <span className={`h-2.5 w-2.5 rounded-full mt-1 ${RAG_BG[iss.rag]}`} title={RAG_LABEL[iss.rag]} />
                </div>
                <div className="flex items-end justify-between">
                  <ScoreRing score={iss.score} rag={iss.rag} size="text-3xl" />
                  <div className="text-[11px] text-muted-foreground text-right">{iss.kpi_count} {iss.kpi_count === 1 ? "KPI" : "KPIs"}<br />{iss.off_track ? <span className="text-red-500">{iss.off_track} off-track</span> : "all on track"}</div>
                </div>
                <Progress value={iss.score} className="h-1.5 mt-2" />
              </div>
            ))}
          </div>
        </TabsContent>

        {/* KPI Register */}
        <TabsContent value="register" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">All KPIs incl. drafts & submitted. Only <span className="text-[#00E65B]">Approved</span> KPIs appear in the Scorecard.</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadPack} data-testid="evidence-pack-btn"><FileCheck2 className="h-4 w-4" /> Auditor Evidence Pack</Button>
              <Button variant="primary" size="sm" onClick={() => { setEditKpi(null); setEditorOpen(true); }} data-testid="scorecard-register-btn"><Plus className="h-4 w-4" /> Register KPI</Button>
            </div>
          </div>
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>KPI</TableHead><TableHead>Material issue</TableHead><TableHead>Owner</TableHead>
                <TableHead className="text-right">Baseline</TableHead><TableHead className="text-right">Current</TableHead>
                <TableHead className="text-right">Target</TableHead><TableHead className="text-right">Progress</TableHead>
                <TableHead className="text-right">Score</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {[...kpis].sort((a, b) => (a.kpi_score ?? 0) - (b.kpi_score ?? 0)).map((k) => {
                  const st = KPI_STATUS[k.status] || KPI_STATUS.approved;
                  return (
                  <TableRow key={k.id} className="hover:bg-surface-hover" data-testid={`scorecard-kpi-row-${k.id}`}>
                    <TableCell className="text-sm font-medium cursor-pointer" onClick={() => setActive(k.id)}>{k.kpi_name}{k.mandatory && <Badge variant="outline" className="ml-2 text-[8px]">mandatory</Badge>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{k.material_issue}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{k.owner}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatNum(k.baseline_value, { decimals: 1 })}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatNum(k.current_value, { decimals: 1 })} <span className="text-muted-foreground">{k.unit}</span></TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatNum(k.target_value, { decimals: 1 })}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{k.progress_pct}%</TableCell>
                    <TableCell className={`text-right font-mono text-xs font-bold ${RAG[k.rag]}`}>{k.kpi_score}</TableCell>
                    <TableCell><Badge variant={st.tone} className="text-[9px]" data-testid={`kpi-status-${k.id}`}>{st.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => navigate(evidenceHref(k))} data-testid={`kpi-evidence-${k.id}`} title="View evidence records"><FileSearch className="h-3 w-3" /> Evidence</Button>
                        <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => { setEditKpi(k); setEditorOpen(true); }} data-testid={`kpi-edit-${k.id}`}>Edit</Button>
                        {k.status === "draft" && <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => kpiAction(k.id, "submit")} data-testid={`kpi-submit-${k.id}`}><Send className="h-3 w-3" /> Submit</Button>}
                        {k.status === "submitted" && canApprove && <>
                          <Button size="sm" variant="primary" className="h-7 text-[10px]" onClick={() => kpiAction(k.id, "approve")} data-testid={`kpi-approve-${k.id}`}><CheckCircle2 className="h-3 w-3" /> Approve</Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => kpiAction(k.id, "reject")} data-testid={`kpi-reject-${k.id}`}><XCircle className="h-3 w-3 text-red-500" /></Button>
                        </>}
                        {k.status === "submitted" && !canApprove && <span className="text-[10px] text-muted-foreground">awaiting approval</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Owners */}
        <TabsContent value="owners" className="mt-4">
          <div className="card-flat overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Owner</TableHead><TableHead className="text-right">KPIs</TableHead><TableHead className="text-right">Avg score</TableHead><TableHead className="text-right">Off-track</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.owner_view.map((o) => (
                  <TableRow key={o.owner} data-testid={`scorecard-owner-${o.owner.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <TableCell className="text-sm font-medium">{o.owner}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{o.kpis}</TableCell>
                    <TableCell className={`text-right font-mono text-xs font-bold ${RAG[o.avg_score >= 75 ? "green" : o.avg_score >= 40 ? "amber" : "red"]}`}>{o.avg_score}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{o.off_track ? <span className="text-red-500">{o.off_track}</span> : "0"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Frameworks */}
        <TabsContent value="frameworks" className="mt-4">
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-3 inline-flex items-center gap-1"><FileCheck2 className="h-3.5 w-3.5" /> Framework disclosure completion</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              {d.framework_completion.map((f) => (
                <div key={f.framework}>
                  <div className="flex justify-between text-xs mb-1"><span>{f.framework}</span><span className="font-mono">{f.disclosed}/{f.total} · {f.completion_pct}%</span></div>
                  <Progress value={f.completion_pct} className="h-1.5" />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Off-track */}
        <TabsContent value="offtrack" className="mt-4 space-y-2">
          {d.off_track.length ? d.off_track.map((k) => (
            <button key={k.id} onClick={() => setActive(k.id)} className="card-flat p-4 w-full text-left hover:border-accent/60 hover:bg-surface-hover transition-colors flex items-center justify-between gap-4" data-testid={`scorecard-offtrack-${k.id}`}>
              <div className="flex items-center gap-3"><AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                <div><div className="font-medium text-sm">{k.kpi_name}</div><div className="text-xs text-muted-foreground">{k.material_issue} · {k.owner} · <Travel t={k.direction_of_travel} /></div></div>
              </div>
              <div className="text-right"><div className="font-mono text-sm font-bold text-red-500">{k.score}</div><div className="text-[10px] text-muted-foreground">{k.progress_pct}% to target</div></div>
            </button>
          )) : <div className="card-flat p-10 text-center text-muted-foreground">No off-track KPIs — all material targets are on or near track.</div>}
        </TabsContent>

        {/* Board summary */}
        <TabsContent value="board" className="mt-4">
          <div className="card-flat p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI board scorecard summary</div>
              <Button variant="primary" size="sm" onClick={genBoard} disabled={boardBusy} data-testid="scorecard-board-generate">{boardBusy ? "Generating…" : board ? "Regenerate" : "Generate"}</Button>
            </div>
            <div className="text-[11px] text-muted-foreground mb-2" data-testid="scorecard-board-source-note">
              Scored from: <span className="font-medium text-foreground">{siGoverns ? "Score Intelligence (coverage-normalised)" : "your approved ESG Scorecard (KPI-weighted)"}</span> — the same source as the headline above.{board && boardBasis ? ` Basis: ${boardBasis}.` : ""}
            </div>
            {board ? <p className="text-sm leading-relaxed" data-testid="scorecard-board-text">{board}</p>
              : <p className="text-sm text-muted-foreground">Generate an executive-ready summary of overall ESG score, pillar performance, on/off-track KPIs and priority actions.</p>}
          </div>
        </TabsContent>
      </Tabs>

      <KpiDialog kpiId={active} open={!!active} onClose={() => setActive(null)} onChanged={load} />
      <KpiEditor open={editorOpen} onClose={() => setEditorOpen(false)} kpi={editKpi} onSaved={load} />
    </div>
  );
}
